import { useCallback, useEffect, useState } from 'react';
import { apiJson } from '../api/client.js';
import { formatDateTime } from '../utils/formatDate.js';

const STATUS_LABELS = {
  pending: 'Awaiting client',
  approved: 'Approved',
  changes_requested: 'Changes requested',
  cancelled: 'Cancelled',
};

function statusBadgeClass(status) {
  if (status === 'pending') return 'badge-warn';
  if (status === 'approved') return 'badge-ok';
  if (status === 'changes_requested') return 'badge-err';
  return 'badge';
}

/**
 * @param {{ projectId: string | number, figmaUrl?: string }} props
 */
export function FigmaApprovalAdminPanel({ projectId, figmaUrl }) {
  const [data, setData] = useState({ pending: null, history: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [title, setTitle] = useState('');
  const [staffNote, setStaffNote] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await apiJson(`/api/admin/projects/${projectId}/figma-approvals`);
      setData(res);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    void load();
  }, [load]);

  const sendForApproval = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      await apiJson(`/api/admin/projects/${projectId}/figma-approvals`, {
        method: 'POST',
        body: {
          title: title.trim() || null,
          staff_note: staffNote.trim() || null,
        },
      });
      setTitle('');
      setStaffNote('');
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const cancelPending = async () => {
    if (!window.confirm('Cancel the pending approval request?')) return;
    setBusy(true);
    setError('');
    try {
      await apiJson(`/api/admin/projects/${projectId}/figma-approvals/pending`, { method: 'DELETE' });
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const hasUrl = Boolean(figmaUrl?.trim());
  const pending = data.pending;
  const history = data.history ?? [];

  return (
    <div className="auth-card flat stack" style={{ marginTop: '1rem' }}>
      <div>
        <h3 style={{ margin: '0 0 0.25rem' }}>Figma approval</h3>
        <p className="muted small" style={{ margin: 0 }}>
          Send the linked Figma mockup to portal users for review. They can approve or request changes with a comment.
        </p>
      </div>

      {error ? <p className="err">{error}</p> : null}
      {loading ? <p className="muted small">Loading approval status…</p> : null}

      {!loading && pending ? (
        <div className="auth-card flat" style={{ borderLeft: '3px solid var(--warn, #f59e0b)' }}>
          <p style={{ margin: '0 0 0.5rem' }}>
            <span className={`badge ${statusBadgeClass(pending.status)}`}>{STATUS_LABELS[pending.status] || pending.status}</span>
            {pending.title ? <strong style={{ marginLeft: '0.5rem' }}>{pending.title}</strong> : null}
          </p>
          {pending.staff_note ? <p className="muted small" style={{ margin: '0 0 0.5rem' }}>{pending.staff_note}</p> : null}
          <p className="muted small" style={{ margin: 0 }}>
            Sent {formatDateTime(pending.created_at)}
            {pending.sent_by_name ? ` by ${pending.sent_by_name}` : ''}
          </p>
          <button type="button" className="btn-secondary" style={{ marginTop: '0.75rem' }} disabled={busy} onClick={() => void cancelPending()}>
            Cancel request
          </button>
        </div>
      ) : null}

      {!loading && !pending ? (
        <form onSubmit={sendForApproval} className="stack">
          {!hasUrl ? (
            <p className="muted small">Save a Figma URL above before sending for approval.</p>
          ) : (
            <>
              <label className="field">
                <span>Mockup title (optional)</span>
                <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Homepage v2" />
              </label>
              <label className="field">
                <span>Message to client (optional)</span>
                <textarea
                  value={staffNote}
                  onChange={(e) => setStaffNote(e.target.value)}
                  rows={2}
                  placeholder="Please review the updated homepage layout."
                />
              </label>
              <button type="submit" className="btn-primary" disabled={busy || !hasUrl}>
                {busy ? 'Sending…' : 'Send for approval'}
              </button>
            </>
          )}
        </form>
      ) : null}

      {!loading && history.length ? (
        <div>
          <h4 style={{ margin: '0.5rem 0' }}>History</h4>
          <ul className="list-plain">
            {history.map((item) => (
              <li key={item.id} className="muted small" style={{ marginBottom: '0.5rem' }}>
                <span className={`badge ${statusBadgeClass(item.status)}`}>{STATUS_LABELS[item.status] || item.status}</span>
                {' '}
                {item.title || 'Mockup'}
                {' · '}
                {formatDateTime(item.created_at)}
                {item.client_comment ? (
                  <div style={{ marginTop: '0.25rem' }}>Client: {item.client_comment}</div>
                ) : null}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}
